import type { OscNote, SfxDef, LoopDef } from '@/domain/audio/types';

/**
 * AudioEngine — wrapper minimalista do WebAudio API.
 * Cria osciladores temporários por SFX, mantém loops em GainNodes dedicados para crossfade.
 */

export interface AudioEngineApi {
  unlock(): Promise<void>;
  isUnlocked(): boolean;
  playSfx(def: SfxDef, masterMult: number): void;
  startLoop(slot: 'ambient' | 'music', def: LoopDef, fadeInMs: number, masterMult: number): void;
  stopLoop(slot: 'ambient' | 'music', fadeOutMs: number): void;
  setCategoryGain(category: 'sfx' | 'ambient' | 'music', value: number): void;
  setMaster(value: number): void;
  setMuted(b: boolean): void;
  dispose(): void;
}

interface LoopState {
  oscs: OscillatorNode[];
  gainNode: GainNode;
  intervalId: ReturnType<typeof setInterval> | null;
  defId: string;
}

class AudioEngineImpl implements AudioEngineApi {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private gainsByCategory: Record<'sfx' | 'ambient' | 'music', GainNode | null> = {
    sfx: null, ambient: null, music: null,
  };
  private loops: { ambient: LoopState | null; music: LoopState | null } = {
    ambient: null, music: null,
  };
  private masterValue = 0.8;
  private muted = false;
  private unlocked = false;

  async unlock(): Promise<void> {
    if (this.unlocked) return;
    if (!this.ctx) {
      const AC = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      if (!AC) return;
      this.ctx = new AC({ latencyHint: 'interactive' });
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = this.muted ? 0 : this.masterValue;
      this.masterGain.connect(this.ctx.destination);
      for (const cat of ['sfx', 'ambient', 'music'] as const) {
        const g = this.ctx.createGain();
        g.gain.value = 1;
        g.connect(this.masterGain);
        this.gainsByCategory[cat] = g;
      }
    }
    if (this.ctx.state === 'suspended') {
      await this.ctx.resume();
    }
    this.unlocked = true;
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  playSfx(def: SfxDef, masterMult: number): void {
    if (!this.ctx || !this.unlocked) return;
    const ctx = this.ctx;
    const out = this.gainsByCategory.sfx;
    if (!out) return;
    const now = ctx.currentTime;
    for (const note of def.notes) {
      this.scheduleNote(ctx, out, note, now, masterMult);
    }
    if (def.noiseAmount && def.noiseAmount > 0) {
      this.scheduleNoise(ctx, out, def.totalDurationMs, def.noiseAmount * masterMult, now);
    }
  }

  startLoop(slot: 'ambient' | 'music', def: LoopDef, fadeInMs: number, masterMult: number): void {
    if (!this.ctx || !this.unlocked) return;
    // Stop existing first (without fade, for simplicity de implementação)
    const existing = this.loops[slot];
    if (existing && existing.defId === def.id) return; // já tocando
    if (existing) {
      this.stopLoopInternal(slot, fadeInMs);
    }
    const ctx = this.ctx;
    const catGain = this.gainsByCategory[def.category];
    if (!catGain) return;
    const loopGain = ctx.createGain();
    loopGain.gain.value = 0;
    loopGain.gain.linearRampToValueAtTime(def.gain * masterMult, ctx.currentTime + fadeInMs / 1000);
    loopGain.connect(catGain);
    const startLoop = (): void => {
      const startAt = ctx.currentTime;
      for (const note of def.pattern) {
        this.scheduleNote(ctx, loopGain, note, startAt, 1);
      }
    };
    startLoop();
    const intervalId = setInterval(startLoop, def.loopDurationMs);
    this.loops[slot] = { oscs: [], gainNode: loopGain, intervalId, defId: def.id };
  }

  stopLoop(slot: 'ambient' | 'music', fadeOutMs: number): void {
    this.stopLoopInternal(slot, fadeOutMs);
  }

  private stopLoopInternal(slot: 'ambient' | 'music', fadeOutMs: number): void {
    const cur = this.loops[slot];
    if (!cur || !this.ctx) return;
    if (cur.intervalId) clearInterval(cur.intervalId);
    const now = this.ctx.currentTime;
    cur.gainNode.gain.cancelScheduledValues(now);
    cur.gainNode.gain.setValueAtTime(cur.gainNode.gain.value, now);
    cur.gainNode.gain.linearRampToValueAtTime(0, now + fadeOutMs / 1000);
    // Schedule disconnect
    setTimeout(() => {
      try {
        cur.gainNode.disconnect();
      } catch {
        /* noop */
      }
    }, fadeOutMs + 100);
    this.loops[slot] = null;
  }

  setCategoryGain(category: 'sfx' | 'ambient' | 'music', value: number): void {
    const g = this.gainsByCategory[category];
    if (g && this.ctx) {
      g.gain.linearRampToValueAtTime(Math.max(0, Math.min(1, value)), this.ctx.currentTime + 0.05);
    }
  }

  setMaster(value: number): void {
    this.masterValue = Math.max(0, Math.min(1, value));
    if (this.masterGain && this.ctx && !this.muted) {
      this.masterGain.gain.linearRampToValueAtTime(this.masterValue, this.ctx.currentTime + 0.05);
    }
  }

  setMuted(b: boolean): void {
    this.muted = b;
    if (this.masterGain && this.ctx) {
      this.masterGain.gain.linearRampToValueAtTime(b ? 0 : this.masterValue, this.ctx.currentTime + 0.05);
    }
  }

  dispose(): void {
    this.stopLoopInternal('ambient', 50);
    this.stopLoopInternal('music', 50);
    if (this.ctx) {
      void this.ctx.close();
      this.ctx = null;
    }
    this.unlocked = false;
  }

  private scheduleNote(
    ctx: AudioContext,
    out: GainNode,
    note: OscNote,
    baseTimeS: number,
    masterMult: number,
  ): void {
    const startS = baseTimeS + (note.startOffsetMs ?? 0) / 1000;
    const durS = note.durationMs / 1000;
    const osc = ctx.createOscillator();
    osc.type = note.type;
    osc.frequency.setValueAtTime(note.freq, startS);
    if (note.detune !== undefined) osc.detune.setValueAtTime(note.detune, startS);

    const noteGain = ctx.createGain();
    const peak = (note.gain ?? 0.3) * masterMult;
    const attack = (note.attackMs ?? 5) / 1000;
    const release = (note.releaseMs ?? 30) / 1000;

    noteGain.gain.setValueAtTime(0, startS);
    noteGain.gain.linearRampToValueAtTime(peak, startS + attack);
    noteGain.gain.setValueAtTime(peak, startS + durS - release);
    noteGain.gain.linearRampToValueAtTime(0, startS + durS);

    if (note.filterFreq !== undefined) {
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.value = note.filterFreq;
      osc.connect(filter);
      filter.connect(noteGain);
    } else {
      osc.connect(noteGain);
    }
    noteGain.connect(out);

    osc.start(startS);
    osc.stop(startS + durS + 0.05);
  }

  private scheduleNoise(
    ctx: AudioContext,
    out: GainNode,
    durationMs: number,
    gain: number,
    baseTimeS: number,
  ): void {
    const buffer = ctx.createBuffer(1, ctx.sampleRate * (durationMs / 1000), ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < data.length; i++) {
      data[i] = Math.random() * 2 - 1;
    }
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0;
    noiseGain.gain.linearRampToValueAtTime(gain * 0.3, baseTimeS + 0.005);
    noiseGain.gain.linearRampToValueAtTime(0, baseTimeS + durationMs / 1000);
    src.connect(noiseGain);
    noiseGain.connect(out);
    src.start(baseTimeS);
  }
}

let _instance: AudioEngineApi | null = null;

export function getAudioEngine(): AudioEngineApi {
  if (!_instance) _instance = new AudioEngineImpl();
  return _instance;
}

export function resetAudioEngineForTests(): void {
  _instance?.dispose();
  _instance = null;
}
